import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppRangePicker from '@/components/common/AppRangePicker';
import { Alert, Select, Spin, Table } from 'antd';
import dayjs from 'dayjs';
import { FiUsers, FiUserCheck } from 'react-icons/fi';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import { MdOutlineGroups, MdOutlineHandshake } from 'react-icons/md';
import DashboardPageWrapper from '../components/layout/DashboardPageWrapper';
import StatCard from '../components/StatCard/StatCard';
import dashboardService from '../services/dashboardService';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSIONS } from '../config/permissions';
import {
  DRILLDOWN_METRICS,
  buildDashboardDrilldownUrl,
  getDrilldownListPath,
} from '../helpers/dashboardDrilldown.helpers';
import { formatFirstName } from '../helpers/name.helpers';

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

/** Indian numbering: 1,00,000 · 1,00,00,000 */
function formatIndianNumber(value, { maximumFractionDigits = 0 } = {}) {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(toNumber(value));
}

function formatInt(value) {
  return formatIndianNumber(value);
}

const ONE_LAKH = 100_000;
const ONE_CRORE = 10_000_000;

function formatRevenue(value) {
  const amount = toNumber(value);
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';

  if (abs >= ONE_CRORE) {
    return `${sign}₹${formatIndianNumber(abs / ONE_CRORE, { maximumFractionDigits: 2 })} Cr`;
  }
  if (abs >= ONE_LAKH) {
    return `${sign}₹${formatIndianNumber(abs / ONE_LAKH, { maximumFractionDigits: 2 })} L`;
  }
  return `${sign}₹${formatIndianNumber(abs, { maximumFractionDigits: 2 })}`;
}

/** Full amount in table cells — no L / Cr shorthand. */
function formatTableRevenue(value) {
  const amount = toNumber(value);
  const sign = amount < 0 ? '-' : '';
  return `${sign}₹${formatIndianNumber(Math.abs(amount), { maximumFractionDigits: 2 })}`;
}

const defaultDateRange = () => [dayjs().startOf('month'), dayjs()];

const teamRevenueColumns = [
  { title: 'Team member', dataIndex: 'rm_name', key: 'rm_name', render: formatFirstName },
  {
    title: 'Partners empanelled',
    dataIndex: 'partners_empanelled',
    key: 'partners_empanelled',
    align: 'right',
    render: (v) => formatInt(v),
  },
  {
    title: 'Partners activated',
    dataIndex: 'partners_activated',
    key: 'partners_activated',
    align: 'right',
    render: (v) => formatInt(v),
  },
  {
    title: 'Net revenue',
    dataIndex: 'net_revenue',
    key: 'net_revenue',
    align: 'right',
    render: (v) => formatTableRevenue(v),
  },
  {
    title: 'Fulfilled orders',
    dataIndex: 'fulfilled_orders',
    key: 'fulfilled_orders',
    align: 'right',
    render: (v) => formatInt(v),
  },
];

export default function DashboardHome() {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canViewInvestors = isSuperAdmin || hasPermission(PERMISSIONS.INVESTORS_LIST_VIEW);
  const canViewPartners = isSuperAdmin || hasPermission(PERMISSIONS.PARTNER_LIST_VIEW);

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [scopeUserId, setScopeUserId] = useState('all');
  const [scopeOptions, setScopeOptions] = useState([{ id: 'all', label: 'All' }]);
  const [viewerScope, setViewerScope] = useState('team');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadScopeOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const payload = await dashboardService.getScopeOptions();
      setScopeOptions(payload?.options?.length ? payload.options : [{ id: 'all', label: 'All' }]);
      setViewerScope(payload?.viewer_scope ?? 'team');
    } catch (err) {
      setScopeOptions([{ id: 'all', label: 'All' }]);
      setViewerScope('team');
      setError(err?.response?.data?.message || err?.message || 'Failed to load scope options.');
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;

    setLoading(true);
    setError('');
    try {
      const data = await dashboardService.getSummary({
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        scopeUserId,
      });
      setSummary(data);
    } catch (err) {
      setSummary(null);
      setError(err?.response?.data?.message || err?.message || 'Failed to load dashboard summary.');
    } finally {
      setLoading(false);
    }
  }, [dateRange, scopeUserId]);

  useEffect(() => {
    loadScopeOptions();
  }, [loadScopeOptions]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleCardClick = (metric) => {
    const path = getDrilldownListPath(metric);
    if (!path || !dateRange?.[0] || !dateRange?.[1]) return;

    if (
      (metric === DRILLDOWN_METRICS.TOTAL_INVESTORS ||
        metric === DRILLDOWN_METRICS.ACTIVE_INVESTORS) &&
      !canViewInvestors
    ) {
      return;
    }
    if (
      (metric === DRILLDOWN_METRICS.TOTAL_PARTNERS ||
        metric === DRILLDOWN_METRICS.ACTIVE_PARTNERS) &&
      !canViewPartners
    ) {
      return;
    }

    navigate(
      buildDashboardDrilldownUrl({
        path,
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD'),
        metric,
        scopeUserId,
      })
    );
  };

  const cards = [
    {
      key: DRILLDOWN_METRICS.TOTAL_INVESTORS,
      label: 'Total Investors',
      value: formatInt(summary?.total_investors),
      icon: <FiUsers />,
      cardClass: 'border-blue-100',
      valueClass: 'text-blue-700',
      clickable: canViewInvestors,
      onClick: () => handleCardClick(DRILLDOWN_METRICS.TOTAL_INVESTORS),
    },
    {
      key: DRILLDOWN_METRICS.TOTAL_PARTNERS,
      label: 'Total Partners',
      value: formatInt(summary?.total_partners),
      icon: <MdOutlineHandshake />,
      cardClass: 'border-violet-100',
      valueClass: 'text-violet-700',
      clickable: canViewPartners,
      onClick: () => handleCardClick(DRILLDOWN_METRICS.TOTAL_PARTNERS),
    },
    {
      key: DRILLDOWN_METRICS.ACTIVE_PARTNERS,
      label: 'Active Partners',
      value: formatInt(summary?.active_partners),
      icon: <MdOutlineGroups />,
      cardClass: 'border-amber-100',
      valueClass: 'text-amber-700',
      clickable: canViewPartners,
      onClick: () => handleCardClick(DRILLDOWN_METRICS.ACTIVE_PARTNERS),
    },
    {
      key: DRILLDOWN_METRICS.ACTIVE_INVESTORS,
      label: 'Active Investors',
      value: formatInt(summary?.active_investors),
      icon: <FiUserCheck />,
      cardClass: 'border-teal-100',
      valueClass: 'text-teal-700',
      clickable: canViewInvestors,
      onClick: () => handleCardClick(DRILLDOWN_METRICS.ACTIVE_INVESTORS),
    },
    {
      key: 'net_revenue',
      label: viewerScope === 'platform' && scopeUserId === 'all' ? 'Altcase Revenue' : 'Net Revenue',
      value: formatRevenue(summary?.net_revenue),
      icon: <RiMoneyDollarCircleLine />,
      cardClass: 'border-emerald-100',
      valueClass: 'text-emerald-700',
      clickable: false,
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500 max-w-[500px]">
              {viewerScope === 'platform'
                ? scopeUserId === 'all'
                  ? 'Platform-wide metrics. Altcase revenue is the sum of platform margin (price_partner − altcase_price) across all fulfilled orders.'
                  : 'Platform-wide metrics. Net revenue uses orders tagged to each RM. Active metrics use the selected date range.'
                : 'Selecting a leader shows org total on the cards and each person’s revenue in the table below (full amounts, no abbreviations).'}{' '}
              Click a KPI card to open the matching list with filters applied.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <AppRangePicker
              value={dateRange}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDateRange(range);
              }}
              allowClear={false}
              format="DD MMM YYYY"
              presets={[
                { label: 'Last 7 days', value: [dayjs().subtract(6, 'day'), dayjs()] },
                { label: 'Last 30 days', value: [dayjs().subtract(29, 'day'), dayjs()] },
                { label: 'Month to date', value: [dayjs().startOf('month'), dayjs()] },
              ]}
            />
            <Select
              className="min-w-[200px]"
              value={scopeUserId}
              loading={optionsLoading}
              onChange={setScopeUserId}
              options={scopeOptions.map((opt) => ({
                value: opt.id,
                label: opt.label,
              }))}
            />
          </div>
        </div>

        {error ? (
          <Alert type="error" message={error} showIcon />
        ) : null}

        <Spin spinning={loading}>
          <div className="overflow-x-auto">
            <div className="grid min-w-[960px] grid-cols-5 gap-4">
              {cards.map((card) => (
                <StatCard
                  key={card.key}
                  label={card.label}
                  value={card.value}
                  icon={card.icon}
                  cardClass={card.cardClass}
                  valueClass={card.valueClass}
                  clickable={card.clickable}
                  onClick={card.onClick}
                />
              ))}
            </div>
          </div>
        </Spin>

        {summary?.team_revenue_breakdown?.length > 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-base font-semibold text-gray-900">Team revenue by person</h2>
            <Table
              rowKey="rm_uuid"
              columns={teamRevenueColumns}
              dataSource={summary.team_revenue_breakdown}
              pagination={false}
              size="small"
              loading={loading}
            />
          </div>
        ) : null}
      </div>
    </DashboardPageWrapper>
  );
}
